import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    X, Globe, Network, Database, CheckCircle,
    ChevronRight, ChevronLeft, FileText, Zap, ShieldCheck,
    Terminal, AlertCircle, AlertTriangle, Eye, EyeOff, Server, Hash,
    User, Lock, ArrowRight, Check, HelpCircle, Upload, Trash2, XCircle, Save
} from 'lucide-react';
import './DMSWizard.css';
import { FieldHelpPopover } from './ConnectionWizardHelp';
import ContextHelp from '../ContextHelp';
import { API_BASE_URL } from '../../config';
import { validateName, validateHost } from '../../utils/validators';

// ── Constants ─────────────────────────────────────────────────────────────────

const WIZARD_STEPS = [
    {
        id: 1,
        title: 'Define Source Connection',
        shortTitle: 'Source Connection',
        description: 'Configure your source database connection details',
    },
    {
        id: 2,
        title: 'Define Destination Connection',
        shortTitle: 'Destination Connection',
        description: 'Configure your destination database connection details',
    },
    {
        id: 3,
        title: 'Review Configuration',
        shortTitle: 'Review Configuration',
        description: 'Verify all settings before creating the connections',
    },
    {
        id: 4,
        title: 'Create Connection',
        shortTitle: 'Create Connection',
        description: 'Finalize and create your database connections',
    },
];



const emptyConn = (role) => ({
    role,
    name: '',
    type: 'postgresql',
    database_engine: '',
    host: '',
    port: 5432,
    database: '',
    username: '',
    password: '',
    ssl_enabled: false,
    ssl_mode: 'require',
    connectivity_mode: 'public',
    public_connectivity_method: 'direct',
    private_connectivity_method: 'ssh',
    use_ssh_tunnel: false,
    ssh_host: '',
    ssh_port: 22,
    ssh_username: 'ec2-user',
    ssh_private_key: '',
    region: '',
    cloud_provider: 'on-premise',
});

function normalizeConnectionData(conn) {
    if (!conn) return conn;
    const isSsh = Boolean(conn.use_ssh_tunnel || conn.public_connectivity_method === 'ssh' || (conn.ssh_host && conn.ssh_host.trim().length > 0));
    const engine = (conn.database_engine || '').toLowerCase();
    const type = (conn.type || '').toLowerCase();
    let dbName = conn.database_name || conn.database || '';
    if (type === 'mssql' || engine.includes('mssql') || (engine.includes('sql server') && !engine.includes('mysql'))) {
        if (!dbName || dbName === 'mysql' || dbName === 'postgres') {
            dbName = 'master';
        }
    } else if (type === 'mysql' || engine.includes('mysql')) {
        if (!dbName || dbName === 'master' || dbName === 'postgres') {
            dbName = 'mysql';
        }
    } else if (type === 'postgresql' || engine.includes('postgre')) {
        if (!dbName || dbName === 'mysql' || dbName === 'master') {
            dbName = 'postgres';
        }
    }
    return {
        ...conn,
        database_name: dbName,
        database: dbName,
        use_ssh_tunnel: isSsh,
        public_connectivity_method: isSsh ? 'ssh' : (conn.public_connectivity_method || 'direct'),
    };
}

const fieldLabels = {
    name: 'Connection Name',
    database_engine: 'Database Engine',
    region: 'Region',
    host: 'Host / IP Address',
    port: 'Port',
    username: 'Username',
    password: 'Password'
};

function validateForm(form) {
    const errors = {};
    const req = ['name', 'database_engine', 'region', 'host', 'port', 'username', 'password'];
    req.forEach(f => {
        if (!form[f] || String(form[f]).trim() === '') {
            errors[f] = `${fieldLabels[f]} is required.`;
        }
    });
    if (form.port && (isNaN(Number(form.port)) || Number(form.port) < 1 || Number(form.port) > 65535)) {
        errors.port = 'Must be between 1 and 65535';
    }
    if (form.host && !errors.host) {
        const hostLower = form.host.toLowerCase();
        const engineLower = (form.database_engine || '').toLowerCase();
        
        const isAwsHost = hostLower.includes('.rds.amazonaws.com');
        const isAzureHost = hostLower.includes('.database.azure.com') || hostLower.includes('.database.windows.net');
        const isGcpHost = hostLower.includes('.sql.goog');

        if (isAwsHost && !engineLower.includes('rds') && !engineLower.includes('aws')) {
            errors.host = "Host is an AWS RDS endpoint, but you selected a different engine.";
        } else if (isAzureHost && !engineLower.includes('azure')) {
            errors.host = "Host is an Azure Database endpoint, but you selected a different engine.";
        } else if (isGcpHost && !engineLower.includes('cloud sql') && !engineLower.includes('gcp')) {
            errors.host = "Host is a GCP Cloud SQL endpoint, but you selected a different engine.";
        }
    }
    return errors;
}

function isValid(form) {
    return Object.keys(validateForm(form)).length === 0;
}

// ── Field Component ──────────────────────────────────────────────────────────

function FormField({ label, error, required, children, hint, helpId, isActiveHelp, onHelpClick, cloudProvider, databaseEngine, isLocked }) {
    return (
        <div className="form-group" style={{ marginBottom: '1.25rem', position: 'relative' }}>
            <label style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontSize: '0.875rem',
                fontWeight: 600,
                marginBottom: '0.5rem',
                color: 'var(--text-primary)',
            }}>
                <span>
                    {label}
                    {required && <span style={{ color: '#ef4444', marginLeft: 4 }}>*</span>}
                </span>
                {helpId && (
                    <button
                        type="button"
                        onClick={() => onHelpClick && onHelpClick(helpId)}
                        className={`field-info-btn ${isActiveHelp ? 'active' : ''}`}
                        title={`Get help for ${label}`}
                        style={{
                            background: 'none', border: 'none', padding: 0,
                            color: isActiveHelp ? 'var(--primary)' : 'var(--text-muted)',
                            cursor: 'pointer', display: 'flex', alignItems: 'center',
                            transition: 'color 0.2s', outline: 'none'
                        }}
                    >
                        <HelpCircle size={14} />
                    </button>
                )}
                {isActiveHelp && helpId && (
                    <FieldHelpPopover
                        fieldId={helpId}
                        cloudProvider={cloudProvider}
                        databaseEngine={databaseEngine}
                        onClose={() => onHelpClick && onHelpClick(null)}
                    />
                )}
            </label>
            {children}
            {hint && !error && (
                <small style={{ display: 'block', marginTop: '0.25rem', color: 'var(--text-muted)', fontSize: '0.78rem' }}>
                    {hint}
                </small>
            )}
            {error && (
                <div style={{
                    display: 'flex', alignItems: 'center', gap: 5,
                    marginTop: '0.3rem', color: '#ef4444', fontSize: '0.78rem',
                }}>
                    <AlertCircle size={12} />
                    {error}
                </div>
            )}
        </div>
    );
}

// ── Connection Form ──────────────────────────────────────────────────────────

function ConnectionForm({ data, onChange, errors, cloudRegions = { aws: [], gcp: [], azure: [] }, vpnStatus, activeHelpField, setActiveHelpField, onNavigateToPrivateConnectivity }) {
    const [showPw, setShowPw] = useState(false);
    const [touched, setTouched] = useState({});
    const isExistingConnection = false; // Edit restriction removed, all fields editable

    useEffect(() => {
        if (errors && errors._forceTouch) {
            const allTouched = {};
            ['name', 'database_engine', 'region', 'host', 'port', 'username', 'password'].forEach(f => {
                allTouched[f] = true;
            });
            setTouched(allTouched);
        }
    }, [errors]);

    const handleBlur = (field) => {
        setTouched(prev => ({ ...prev, [field]: true }));
    };

    const getError = (field) => ((touched[field] || errors?._forceTouch) && errors?.[field]) ? errors?.[field] : null;

    const set = useCallback((field, value) => {
        setTouched(prev => ({ ...prev, [field]: true }));
        const next = { ...data, [field]: value };
        if (field === 'database_engine' && value) {
            const v = value.toLowerCase();
            if (v.includes('postgre')) {
                next.type = 'postgresql';
                next.port = 5432;
            } else if (v.includes('mssql') || (v.includes('sql server') && !v.includes('mysql'))) {
                next.type = 'mssql';
                next.port = 1433;
            } else if (v.includes('mysql')) {
                next.type = 'mysql';
                next.port = 3306;
            }
            if (v.includes('amazon') || v.includes('aws')) next.cloud_provider = 'aws';
            else if (v.includes('cloud sql') || v.includes('gcp')) next.cloud_provider = 'gcp';
            else if (v.includes('azure')) next.cloud_provider = 'azure';
            else {
                next.cloud_provider = 'on-premise';
                next.region = 'on-premise';
            }
        }
        if (field === 'ssl_enabled') next.ssl_mode = value ? 'require' : 'disable';
        if (field === 'connectivity_mode' && value === 'public') {
            next.connectivity_mode = 'public';
            const method = next.public_connectivity_method || (next.use_ssh_tunnel ? 'ssh' : 'direct');
            next.public_connectivity_method = method;
            next.use_ssh_tunnel = method === 'ssh';
        }
        if (field === 'connectivity_mode' && value === 'private') {
            next.connectivity_mode = 'private';
            next.private_connectivity_method = 'vpn';
            next.use_ssh_tunnel = false;
        }
        if (field === 'public_connectivity_method') {
            next.public_connectivity_method = value;
            next.use_ssh_tunnel = value === 'ssh';
        }
        onChange(next);
    }, [data, onChange]);



    const formControlStyle = (hasError) => ({
        width: '100%',
        marginTop: '0.25rem',
        padding: '0.625rem 0.875rem',
        borderRadius: 8,
        fontSize: '0.9rem',
        boxSizing: 'border-box',
        transition: 'border-color 0.2s, box-shadow 0.2s',
        border: hasError
            ? '1px solid #ef4444'
            : '1px solid var(--border-color)',
        background: 'var(--bg-input, #0f172a)',
        color: 'var(--text-primary)',
        outline: 'none',
    });

    const isPublic = data.connectivity_mode !== 'private';
    const isPrivate = data.connectivity_mode === 'private';
    const isSsh = isPublic && (data.public_connectivity_method === 'ssh' || data.use_ssh_tunnel);
    const isVpn = isPrivate;

    const keyFileInputRef = useRef(null);
    const [uploadedKeyFileName, setUploadedKeyFileName] = useState('');

    const handleKeyFileUpload = (e) => {
        const file = e.target.files && e.target.files[0];
        if (!file) return;
        setUploadedKeyFileName(file.name);
        const reader = new FileReader();
        reader.onload = (event) => {
            const content = event.target.result;
            set('ssh_private_key', content);
        };
        reader.readAsText(file);
    };

    const getHelpId = (base) => isPrivate ? `${base}_private` : `${base}_public`;

    const awsRegionsList = cloudRegions?.aws || [];
    const gcpRegionsList = cloudRegions?.gcp || [];
    const azureRegionsList = cloudRegions?.azure || [];

    return (
        <div>
            {/* Connection Name */}
            <FormField label="Connection Name" error={getError('name')} required
                hint="Choose a descriptive name for this database connection">
                <input
                    type="text"
                    className="form-control"
                    style={formControlStyle(!!getError('name'))}
                    placeholder="e.g., Production PostgreSQL"
                    value={data.name}
                    onChange={e => set('name', e.target.value)}
                    onBlur={() => handleBlur('name')}
                />
            </FormField>

            {/* Connectivity Mode */}
            <FormField label="Connectivity Mode" isLocked={isExistingConnection}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: '0.25rem' }}>
                    {[
                        { val: 'public', icon: <Globe size={15} />, label: 'Public Internet', desc: 'Direct IP or SSH tunnel' },
                        { val: 'private', icon: <Network size={15} />, label: 'Private Network', desc: 'Dedicated VPN Tunnel' },
                    ].map(opt => (
                        <button key={opt.val} type="button"
                            onClick={() => !isExistingConnection && set('connectivity_mode', opt.val)}
                            style={{
                                padding: '0.75rem 1rem',
                                borderRadius: 10,
                                border: `2px solid ${data.connectivity_mode === opt.val ? '#7c3aed' : 'var(--border-color)'}`,
                                background: data.connectivity_mode === opt.val
                                    ? 'rgba(124,58,237,0.1)'
                                    : 'var(--bg-input, rgba(15,23,42,0.6))',
                                cursor: isExistingConnection ? 'not-allowed' : 'pointer',
                                opacity: isExistingConnection && data.connectivity_mode !== opt.val ? 0.5 : 1,
                                textAlign: 'left',
                                transition: 'all 0.2s',
                            }}>
                            <div style={{
                                display: 'flex', alignItems: 'center', gap: 7,
                                color: data.connectivity_mode === opt.val ? '#a78bfa' : 'var(--text-primary)',
                                fontWeight: 600, fontSize: '0.875rem', marginBottom: 3,
                            }}>
                                {opt.icon} {opt.label}
                            </div>
                            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{opt.desc}</div>
                        </button>
                    ))}
                </div>
            </FormField>

            {/* Public Connectivity Method Selector */}
            {isPublic && (
                <FormField label="Public Connectivity Method" isLocked={isExistingConnection}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: '0.25rem' }}>
                        {[
                            { val: 'direct', icon: <Globe size={14} />, label: 'Direct Connection', desc: 'Public IP or domain' },
                            { val: 'ssh', icon: <Terminal size={14} />, label: 'Managed SSH', desc: 'Bastion host tunnel' },
                        ].map(opt => {
                            const selected = (data.public_connectivity_method || (data.use_ssh_tunnel ? 'ssh' : 'direct')) === opt.val;
                            return (
                                <div key={opt.val}
                                    onClick={() => !isExistingConnection && set('public_connectivity_method', opt.val)}
                                    style={{
                                        padding: '0.75rem 1rem',
                                        borderRadius: 8,
                                        border: `2px solid ${selected ? '#7c3aed' : 'transparent'}`,
                                        background: selected
                                            ? 'rgba(124,58,237,0.08)'
                                            : 'var(--bg-input, rgba(15,23,42,0.4))',
                                        cursor: isExistingConnection ? 'not-allowed' : 'pointer',
                                        opacity: isExistingConnection && !selected ? 0.5 : 1,
                                        boxShadow: `inset 0 0 0 1px ${selected ? 'transparent' : 'var(--border-color)'}`,
                                        transition: 'all 0.2s',
                                    }}>
                                    <div style={{
                                        display: 'flex', alignItems: 'center', gap: 7,
                                        color: selected ? '#a78bfa' : 'var(--text-secondary)',
                                        fontWeight: 600, fontSize: '0.82rem', marginBottom: 2,
                                    }}>
                                        {opt.icon} {opt.label}
                                    </div>
                                    <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>{opt.desc}</div>
                                </div>
                            );
                        })}
                    </div>
                </FormField>
            )}

            {/* ── Database Instance ──────────────────────────── */}
            <div style={{
                fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase',
                letterSpacing: '0.06em', color: 'var(--text-muted)',
                display: 'flex', alignItems: 'center', gap: 6,
                paddingBottom: '0.5rem',
                borderBottom: '1px solid var(--border-color)',
                marginBottom: '1rem', marginTop: '1.5rem',
            }}>
                <Database size={13} /> Database Instance
            </div>

            {/* Engine + Region */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <FormField label="Database Engine" error={getError('database_engine')} required isLocked={isExistingConnection}>
                    <select
                        className="form-control"
                        style={formControlStyle(!!getError('database_engine'))}
                        value={data.database_engine || ''}
                        onChange={e => set('database_engine', e.target.value)}
                        onBlur={() => handleBlur('database_engine')}
                        disabled={isExistingConnection}
                    >
                        <option value="">Select Engine…</option>
                        <optgroup label="AWS Managed">
                            <option value="Amazon RDS for PostgreSQL">Amazon RDS for PostgreSQL</option>
                            <option value="Amazon RDS for MySQL">Amazon RDS for MySQL</option>
                            <option value="Amazon RDS for SQL Server">Amazon RDS for SQL Server</option>
                        </optgroup>
                        <optgroup label="GCP Managed">
                            <option value="Cloud SQL for PostgreSQL">Cloud SQL for PostgreSQL</option>
                            <option value="Cloud SQL for MySQL">Cloud SQL for MySQL</option>
                            <option value="Cloud SQL for SQL Server">Cloud SQL for SQL Server</option>
                        </optgroup>
                        <optgroup label="Azure Managed">
                            <option value="Azure Database for PostgreSQL">Azure Database for PostgreSQL</option>
                            <option value="Azure Database for MySQL">Azure Database for MySQL</option>
                            <option value="Azure SQL Database">Azure SQL Database</option>
                        </optgroup>
                        <optgroup label="Generic / Self-Hosted">
                            <option value="PostgreSQL">PostgreSQL</option>
                            <option value="MySQL">MySQL</option>
                            <option value="SQL Server">SQL Server</option>
                        </optgroup>
                    </select>
                </FormField>

                <FormField label={<span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>Region <ContextHelp field="Region" provider={data.cloud_provider} /></span>} error={getError('region')} required isLocked={isExistingConnection}>
                    <select
                        className="form-control"
                        style={formControlStyle(!!getError('region'))}
                        value={data.region || ''}
                        onChange={e => set('region', e.target.value)}
                        onBlur={() => handleBlur('region')}
                        disabled={isExistingConnection}
                    >
                        <option value="">Select Region…</option>
                        {(!data.cloud_provider || data.cloud_provider === 'aws') && awsRegionsList?.length > 0 && (
                            <optgroup label="AWS Regions">
                                {awsRegionsList.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                            </optgroup>
                        )}
                        {(!data.cloud_provider || data.cloud_provider === 'gcp') && gcpRegionsList?.length > 0 && (
                            <optgroup label="GCP Regions">
                                {gcpRegionsList.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                            </optgroup>
                        )}
                        {(!data.cloud_provider || data.cloud_provider === 'azure') && azureRegionsList?.length > 0 && (
                            <optgroup label="Azure Regions">
                                {azureRegionsList.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                            </optgroup>
                        )}
                        <option value="on-premise">On-Premise / Other</option>
                    </select>
                </FormField>
            </div>

            {/* Host + Port */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 100px', gap: 12 }}>
                <FormField
                    label="Host / IP Address" error={getError('host')} required
                    helpId={getHelpId('host')} isActiveHelp={activeHelpField === getHelpId('host')} onHelpClick={setActiveHelpField}
                    cloudProvider={data.cloud_provider} databaseEngine={data.database_engine}
                    isLocked={isExistingConnection}
                    hint={
                        data.host && (
                            data.host.startsWith('10.') || 
                            data.host.startsWith('192.168.') || 
                            (data.host.startsWith('172.') && (() => {
                                const parts = data.host.split('.');
                                if (parts.length >= 2) {
                                    const octet2 = parseInt(parts[1], 10);
                                    return octet2 >= 16 && octet2 <= 31;
                                }
                                return false;
                            })())
                        ) ? "Note: Private IP detected. Ensure private connectivity (VPN/SSH) is configured." : null
                    }
                >
                    <input
                        type="text"
                        className="form-control"
                        style={formControlStyle(!!getError('host'))}
                        placeholder={
                            data.cloud_provider === 'aws'
                                ? "e.g., db.c12345.us-east-1.rds.amazonaws.com / 10.0.1.50"
                                : data.cloud_provider === 'azure'
                                ? "e.g., mydb.postgres.database.azure.com / 10.0.1.50"
                                : "e.g., db.example.com / 192.168.1.100"
                        }
                        value={data.host}
                        onChange={e => set('host', e.target.value)}
                        onBlur={() => handleBlur('host')}
                        disabled={isExistingConnection}
                    />
                </FormField>
                <FormField label="Port" error={getError('port')} required
                    helpId={getHelpId('port')} isActiveHelp={activeHelpField === getHelpId('port')} onHelpClick={setActiveHelpField}
                    cloudProvider={data.cloud_provider} databaseEngine={data.database_engine}
                    isLocked={isExistingConnection}
                >
                    <input
                        type="number"
                        className="form-control"
                        style={formControlStyle(!!getError('port'))}
                        placeholder="5432"
                        value={data.port}
                        onChange={e => set('port', e.target.value)}
                        onBlur={() => handleBlur('port')}
                        disabled={isExistingConnection}
                    />
                </FormField>
            </div>



            {/* ── Credentials ──────────────────────────────────── */}
            <div style={{
                fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase',
                letterSpacing: '0.06em', color: 'var(--text-muted)',
                display: 'flex', alignItems: 'center', gap: 6,
                paddingBottom: '0.5rem',
                borderBottom: '1px solid var(--border-color)',
                marginBottom: '1rem', marginTop: '1.5rem',
            }}>
                <Lock size={13} /> Credentials
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <FormField label="Username" error={getError('username')} required
                    helpId={getHelpId('username')} isActiveHelp={activeHelpField === getHelpId('username')} onHelpClick={setActiveHelpField}
                    cloudProvider={data.cloud_provider} databaseEngine={data.database_engine}
                >
                    <input
                        type="text"
                        className="form-control"
                        style={formControlStyle(!!getError('username'))}
                        placeholder="db_user"
                        value={data.username}
                        onChange={e => set('username', e.target.value)}
                        onBlur={() => handleBlur('username')}
                    />
                </FormField>
                <FormField label={data.id ? "Password (optional)" : "Password"} error={getError('password')} required={!data.id}>
                    <div style={{ position: 'relative' }}>
                        <input
                            type={showPw ? 'text' : 'password'}
                            className="form-control"
                            style={{ ...formControlStyle(!!getError('password')), paddingRight: 40 }}
                            placeholder={data.id ? "•••••••• (leave blank to keep current)" : "••••••••"}
                            value={data.password}
                            onChange={e => set('password', e.target.value)}
                            onBlur={() => handleBlur('password')}
                        />
                        <button type="button" onClick={() => setShowPw(v => !v)}
                            style={{
                                position: 'absolute', right: 10, top: '50%',
                                transform: 'translateY(-50%)',
                                background: 'none', border: 'none', cursor: 'pointer',
                                color: 'var(--text-muted)', padding: 0, display: 'flex',
                            }}>
                            {showPw ? <EyeOff size={14} /> : <Eye size={14} />}
                        </button>
                    </div>
                </FormField>
            </div>

            {/* SSL */}
            <div style={{
                padding: '0.875rem 1rem',
                borderRadius: 10,
                background: 'rgba(59,130,246,0.05)',
                border: '1px solid rgba(59,130,246,0.12)',
                marginBottom: '1rem',
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                    <label style={{
                        display: 'flex', alignItems: 'center', gap: 8, cursor: isExistingConnection ? 'not-allowed' : 'pointer',
                        userSelect: 'none', fontSize: '0.875rem',
                        color: 'var(--text-primary)', fontWeight: 600,
                        opacity: isExistingConnection ? 0.6 : 1
                    }}>
                        <input
                            type="checkbox"
                            checked={data.ssl_enabled}
                            onChange={e => set('ssl_enabled', e.target.checked)}
                            style={{ width: 'auto', accentColor: '#6366f1' }}
                            disabled={isExistingConnection}
                        />
                        <ShieldCheck size={14} style={{ color: '#60a5fa' }} />
                        Enable SSL / TLS
                    </label>
                    {data.ssl_enabled && (
                        <select
                            className="form-control"
                            value={data.ssl_mode || 'require'}
                            onChange={e => set('ssl_mode', e.target.value)}
                            style={{ width: 'auto', flex: 1, minWidth: 150, marginTop: 0, fontSize: '0.85rem' }}
                            disabled={isExistingConnection}
                        >
                            <option value="require">Require</option>
                            <option value="prefer">Prefer</option>
                            <option value="allow">Allow</option>
                            <option value="verify-ca">Verify CA</option>
                            <option value="verify-full">Verify Full</option>
                            <option value="disable">Disable</option>
                        </select>
                    )}
                </div>
            </div>

            {/* SSH Fields */}
            {isSsh && (
                <div style={{
                    padding: '1rem', borderRadius: 10,
                    background: 'var(--bg-input, rgba(15,23,42,0.4))',
                    border: '1px solid var(--border-color)',
                    marginBottom: '1rem',
                }}>
                    <div style={{
                        fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase',
                        letterSpacing: '0.06em', color: 'var(--text-muted)',
                        display: 'flex', alignItems: 'center', gap: 6,
                        paddingBottom: '0.75rem',
                        borderBottom: '1px solid var(--border-color)',
                        marginBottom: '0.75rem',
                    }}>
                        <Terminal size={13} /> SSH Tunnel
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 80px', gap: 10 }}>
                        <FormField label="Bastion Host / IP Address"
                            helpId="bastion_host" isActiveHelp={activeHelpField === 'bastion_host'} onHelpClick={setActiveHelpField}
                            cloudProvider={data.cloud_provider} databaseEngine={data.database_engine}
                            isLocked={isExistingConnection}
                        >
                            <input type="text" className="form-control"
                                style={formControlStyle(false)}
                                placeholder="e.g., bastion.example.com / 198.51.100.24"
                                value={data.ssh_host}
                                onChange={e => set('ssh_host', e.target.value)}
                                disabled={isExistingConnection} />
                        </FormField>
                        <FormField label="SSH Port" isLocked={isExistingConnection}>
                            <input type="text" className="form-control"
                                style={formControlStyle(false)}
                                placeholder="22"
                                value={data.ssh_port}
                                onChange={e => set('ssh_port', e.target.value)}
                                disabled={isExistingConnection} />
                        </FormField>
                    </div>
                    <FormField label="SSH Username"
                        helpId="ssh_username" isActiveHelp={activeHelpField === 'ssh_username'} onHelpClick={setActiveHelpField}
                        cloudProvider={data.cloud_provider} databaseEngine={data.database_engine}
                        isLocked={isExistingConnection}
                    >
                        <input type="text" className="form-control"
                            style={formControlStyle(false)}
                            placeholder="ubuntu"
                            value={data.ssh_username}
                            onChange={e => set('ssh_username', e.target.value)}
                            disabled={isExistingConnection} />
                    </FormField>
                    <FormField label="Private Key (PEM)"
                        helpId="pem_file" isActiveHelp={activeHelpField === 'pem_file'} onHelpClick={setActiveHelpField}
                        cloudProvider={data.cloud_provider} databaseEngine={data.database_engine}
                        isLocked={isExistingConnection}
                        hint="Upload your unencrypted SSH private key file (.pem or .key)"
                    >
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginTop: 4 }}>
                                <input
                                    type="file"
                                    ref={keyFileInputRef}
                                    style={{ display: 'none' }}
                                    accept=".pem,.key,.pub,.txt,*"
                                    onChange={handleKeyFileUpload}
                                    disabled={isExistingConnection}
                                />
                                <button
                                    type="button"
                                    onClick={() => keyFileInputRef.current && keyFileInputRef.current.click()}
                                    disabled={isExistingConnection}
                                    style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: 6,
                                        padding: '0.55rem 1rem',
                                        fontSize: '0.85rem',
                                        borderRadius: 8,
                                        cursor: isExistingConnection ? 'not-allowed' : 'pointer',
                                        background: 'rgba(99, 102, 241, 0.15)',
                                        border: '1px solid rgba(99, 102, 241, 0.35)',
                                        color: '#818cf8',
                                        fontWeight: 600,
                                        transition: 'all 0.2s',
                                    }}
                                >
                                    <Upload size={15} /> Upload Key File (.pem)
                                </button>
                                {uploadedKeyFileName ? (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                        <span style={{ fontSize: '0.8rem', color: '#10b981', display: 'inline-flex', alignItems: 'center', gap: 4, fontWeight: 600 }}>
                                            <CheckCircle size={14} /> {uploadedKeyFileName}
                                        </span>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setUploadedKeyFileName('');
                                                set('ssh_private_key', '');
                                                if (keyFileInputRef.current) keyFileInputRef.current.value = '';
                                            }}
                                            style={{
                                                background: 'none',
                                                border: 'none',
                                                color: '#ef4444',
                                                cursor: 'pointer',
                                                fontSize: '0.78rem',
                                                textDecoration: 'underline',
                                                padding: 0
                                            }}
                                        >
                                            Remove
                                        </button>
                                    </div>
                                ) : (data.ssh_private_key || data.has_ssh_key || data.encrypted_ssh_key) ? (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                        <span style={{ fontSize: '0.8rem', color: '#10b981', display: 'inline-flex', alignItems: 'center', gap: 4, fontWeight: 600 }}>
                                            <CheckCircle size={14} /> {data.ssh_private_key ? `Key Loaded (${data.ssh_private_key.length} bytes)` : 'Saved SSH Key Preserved'}
                                        </span>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setUploadedKeyFileName('');
                                                set('ssh_private_key', '');
                                                if (keyFileInputRef.current) keyFileInputRef.current.value = '';
                                            }}
                                            style={{
                                                background: 'none',
                                                border: 'none',
                                                color: '#ef4444',
                                                cursor: 'pointer',
                                                fontSize: '0.78rem',
                                                textDecoration: 'underline',
                                                padding: 0
                                            }}
                                        >
                                            Remove
                                        </button>
                                    </div>
                                ) : (
                                    <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                                        No key file selected (.pem, .key)
                                    </span>
                                )}
                            </div>
                        </div>
                    </FormField>
                </div>
            )}

            {/* VPN Status */}
            {isVpn && (
                <div style={{
                    padding: '1.25rem', textAlign: 'center', borderRadius: 12,
                    background: vpnStatus?.configured ? 'rgba(16,185,129,0.05)' : 'var(--bg-card, rgba(15,23,42,0.6))',
                    border: `1px solid ${vpnStatus?.configured ? 'rgba(16,185,129,0.15)' : 'rgba(245,158,11,0.2)'}`,
                    marginBottom: '1rem',
                }}>
                    {vpnStatus?.configured ? (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
                            <CheckCircle size={24} color="#10b981" style={{ marginBottom: 4 }} />
                            <div style={{ fontWeight: 600, color: '#10b981', fontSize: '0.9rem' }}>VPN Tunnel Configured</div>
                            <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>Private connectivity is ready.</div>
                        </div>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#f59e0b', fontSize: '0.9rem', fontWeight: 700 }}>
                                <AlertCircle size={20} />
                                <span>VPN Tunnel not configured</span>
                            </div>
                            <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: 6 }}>
                                Configure the dedicated VPN tunnel first under Private Connectivity.
                            </div>
                            <button
                                type="button"
                                className="btn-bridge-configure"
                                onClick={onNavigateToPrivateConnectivity}
                            >
                                <Network size={16} />
                                Configure VPN Tunnel
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

// ── Review Panel ──────────────────────────────────────────────────────────────

function ReviewPanel({ source, dest, onEditSource, onEditDest }) {
    const getMethod = (conn) => {
        if (conn.connectivity_mode === 'private') return 'vpn';
        if (conn.use_ssh_tunnel || conn.public_connectivity_method === 'ssh') return 'ssh';
        return 'direct';
    };

    const srcMethod = getMethod(source);
    const dstMethod = getMethod(dest);

    let topologyWarning = null;
    if (srcMethod !== dstMethod) {
        if ((srcMethod === 'direct' && dstMethod === 'ssh') || (srcMethod === 'ssh' && dstMethod === 'direct')) {
            topologyWarning = {
                title: 'Hybrid Public & SSH Connectivity Warning',
                message: 'One connection uses Direct Public Internet while the other routes through a Managed SSH Bastion Host. Verify that the SSH Bastion Host and DMS runner can resolve and connect to both endpoints.'
            };
        } else if ((srcMethod === 'ssh' && dstMethod === 'vpn') || (srcMethod === 'vpn' && dstMethod === 'ssh')) {
            topologyWarning = {
                title: 'Mixed SSH & Private VPN Topology Warning',
                message: 'One connection uses a Managed SSH Tunnel over Public Internet while the other uses a Private Dedicated VPN Tunnel. Ensure your DMS runner node can route traffic through both the public SSH Bastion and private VPN Gateway simultaneously.'
            };
        } else if ((srcMethod === 'direct' && dstMethod === 'vpn') || (srcMethod === 'vpn' && dstMethod === 'direct')) {
            topologyWarning = {
                title: 'Mixed Public & Private Network Warning',
                message: 'One connection connects directly over Public Internet while the other uses a Private Dedicated VPN. Ensure your DMS runner node has public internet egress alongside private VPN route access.'
            };
        }
    }

    const ConnCard = ({ title, data, color, onEdit }) => {
        const method = getMethod(data);
        const connText = method === 'vpn' ? 'Private Network (Dedicated VPN)' : method === 'ssh' ? 'Public Internet (Managed SSH)' : 'Public Internet (Direct Connection)';
        return (
            <div style={{
                borderRadius: 16, overflow: 'hidden',
                border: `1px solid ${color}33`,
                background: 'var(--bg-card, rgba(15,23,42,0.6))',
                marginBottom: '1.25rem',
            }}>
                <div style={{
                    padding: '0.875rem 1.25rem',
                    background: `${color}12`,
                    borderBottom: `1px solid ${color}22`,
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div style={{ width: 8, height: 8, borderRadius: '50%', background: color }} />
                        <span style={{
                            fontSize: '0.825rem', fontWeight: 700,
                            textTransform: 'uppercase', letterSpacing: '0.06em', color
                        }}>{title}</span>
                    </div>
                    <button onClick={onEdit}
                        style={{
                            background: 'none', border: `1px solid ${color}44`,
                            borderRadius: 6, color, cursor: 'pointer',
                            fontSize: '0.75rem', fontWeight: 600,
                            padding: '3px 10px', transition: 'all 0.2s',
                        }}>
                        Edit
                    </button>
                </div>
                <div style={{ padding: '1rem 1.25rem' }}>
                    {[
                        { icon: <Database size={13} />, label: 'Engine', val: data.database_engine || data.type || '—' },
                        { icon: <Server size={13} />, label: 'Host', val: data.host || '—' },
                        { icon: <Hash size={13} />, label: 'Port', val: data.port || '—' },
                        { icon: <Database size={13} />, label: 'Database', val: data.database || '—' },
                        { icon: <Globe size={13} />, label: 'Connectivity', val: connText },
                        { icon: <User size={13} />, label: 'Username', val: data.username || '—' },
                        { icon: <ShieldCheck size={13} />, label: 'SSL', val: data.ssl_enabled ? `Enabled (${data.ssl_mode || 'require'})` : 'Disabled' },
                    ].map(row => (
                        <div key={row.label} style={{
                            display: 'flex', alignItems: 'center', gap: 8,
                            paddingBottom: '0.6rem', marginBottom: '0.6rem',
                            borderBottom: '1px solid var(--border-color)',
                        }}>
                            <span style={{ color: 'var(--text-muted)', flexShrink: 0, width: 16 }}>{row.icon}</span>
                            <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)', minWidth: 90, flexShrink: 0 }}>{row.label}</span>
                            <span style={{ fontSize: '0.875rem', color: 'var(--text-primary)', fontWeight: 500, wordBreak: 'break-all' }}>{row.val}</span>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    return (
        <div>
            <div style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '0.75rem 1rem',
                background: 'rgba(16,185,129,0.08)',
                border: '1px solid rgba(16,185,129,0.2)',
                borderRadius: 10, marginBottom: '1.5rem',
            }}>
                <CheckCircle size={16} color="#10b981" />
                <span style={{ fontSize: '0.875rem', color: '#10b981', fontWeight: 600 }}>
                    Both connections configured — ready to create
                </span>
            </div>
            {topologyWarning && (
                <div style={{
                    display: 'flex', gap: 10, alignItems: 'flex-start',
                    padding: '0.875rem 1rem',
                    background: 'rgba(245,158,11,0.08)',
                    border: '1px solid rgba(245,158,11,0.3)',
                    borderRadius: 10, fontSize: '0.825rem', color: '#f59e0b',
                    marginBottom: '1.25rem'
                }}>
                    <AlertTriangle size={18} style={{ flexShrink: 0, marginTop: 1 }} />
                    <div>
                        <strong style={{ display: 'block', marginBottom: 2 }}>{topologyWarning.title}</strong>
                        <span>{topologyWarning.message}</span>
                    </div>
                </div>
            )}
            <ConnCard title="Source Database" data={source} color="#818cf8" onEdit={onEditSource} />
            <ConnCard title="Destination Database" data={dest} color="#34d399" onEdit={onEditDest} />
            <div style={{
                display: 'flex', gap: 10, alignItems: 'flex-start',
                padding: '0.875rem 1rem',
                background: 'rgba(251,191,36,0.06)',
                border: '1px solid rgba(251,191,36,0.15)',
                borderRadius: 10, fontSize: '0.8rem', color: 'var(--text-muted)',
            }}>
                <AlertCircle size={14} style={{ color: '#fbbf24', flexShrink: 0, marginTop: 1 }} />
                <span>
                    Passwords are not shown for security.
                    Click <strong style={{ color: '#fbbf24' }}>Create Connection</strong> to save both connections.
                </span>
            </div>
        </div>
    );
}

// ── Final Create Step ─────────────────────────────────────────────────────────

function CreateStep({ loading, created, error }) {
    if (created) {
        return (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '3rem', textAlign: 'center', gap: '1.25rem' }}>
                <div style={{
                    width: 72, height: 72, borderRadius: '50%',
                    background: 'linear-gradient(135deg,#10b981,#059669)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    boxShadow: '0 0 30px rgba(16,185,129,0.4)',
                }}>
                    <Check size={36} color="#fff" strokeWidth={3} />
                </div>
                <div>
                    <h3 style={{ margin: '0 0 0.5rem', fontSize: '1.25rem', fontWeight: 700, color: '#10b981' }}>
                        Connections Created Successfully!
                    </h3>
                    <p style={{ margin: 0, color: 'var(--text-muted)', fontSize: '0.9rem' }}>
                        Source and Destination connections have been saved to your workspace.
                    </p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '3rem', textAlign: 'center', gap: '1rem' }}>
                <div style={{
                    width: 64, height: 64, borderRadius: '50%',
                    background: 'rgba(239,68,68,0.1)', border: '2px solid rgba(239,68,68,0.3)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                    <AlertCircle size={30} color="#ef4444" />
                </div>
                <div>
                    <h3 style={{ margin: '0 0 0.5rem', fontSize: '1.1rem', fontWeight: 700, color: '#ef4444' }}>
                        Connection Failed
                    </h3>
                    <p style={{ margin: 0, color: 'var(--text-muted)', fontSize: '0.875rem' }}>{error}</p>
                </div>
            </div>
        );
    }

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
            <div style={{
                padding: '1.25rem',
                borderRadius: 12,
                background: 'var(--bg-input, rgba(15,23,42,0.6))',
                border: '1px solid var(--border-color)',
            }}>
                <h4 style={{ margin: '0 0 0.75rem', fontSize: '0.95rem', fontWeight: 600, color: 'var(--text-primary)' }}>
                    Connection Readiness Check
                </h4>
                {[
                    { label: 'Source configuration', status: 'ready' },
                    { label: 'Destination configuration', status: 'ready' },
                    { label: 'Network connectivity', status: 'pending' },
                    { label: 'Authentication credentials', status: 'pending' },
                ].map(item => (
                    <div key={item.label} style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        padding: '0.6rem 0',
                        borderBottom: '1px solid var(--border-color)',
                    }}>
                        <span style={{ fontSize: '0.875rem', color: 'var(--text-primary)' }}>{item.label}</span>
                        <span style={{
                            fontSize: '0.75rem', fontWeight: 600,
                            color: item.status === 'ready' ? '#10b981' : 'var(--text-muted)',
                            display: 'flex', alignItems: 'center', gap: 5,
                        }}>
                            {item.status === 'ready'
                                ? <><CheckCircle size={13} /> Configured</>
                                : <><Zap size={13} /> Will validate on create</>
                            }
                        </span>
                    </div>
                ))}
            </div>

            <div style={{
                padding: '1rem',
                borderRadius: 10,
                background: 'rgba(99,102,241,0.06)',
                border: '1px solid rgba(99,102,241,0.15)',
                display: 'flex', gap: 10, fontSize: '0.825rem', color: 'var(--text-secondary)',
            }}>
                <Zap size={15} style={{ color: '#818cf8', flexShrink: 0, marginTop: 1 }} />
                <span>
                    Clicking <strong style={{ color: '#a78bfa' }}>Create Connection</strong> will save both the Source and Destination connections to your workspace.
                    You can then use them to create migration jobs.
                </span>
            </div>

            {loading && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, justifyContent: 'center', padding: '1.5rem' }}>
                    <div className="spin" style={{ display: 'flex' }}>
                        <Zap size={20} color="#a78bfa" />
                    </div>
                    <span style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>Creating connections…</span>
                </div>
            )}
        </div>
    );
}

// ── Main Wizard ───────────────────────────────────────────────────────────────

export default function ConnectionWizard({
    isOpen,
    onClose,
    onSaveDraft,
    onCreateConnections,
    loading,
    cloudRegions,
    vpnStatus,
    editingData = null,
    onNavigateToPrivateConnectivity,
    editContext = null,
    onUpdateSingleConnection,
    existingConnections = []
}) {
    const [step, setStep] = useState(1);
    const [source, setSource] = useState(emptyConn('source'));
    const [dest, setDest] = useState(emptyConn('destination'));
    const [srcErrors, setSrcErrors] = useState({});
    const [dstErrors, setDstErrors] = useState({});
    const [touched, setTouched] = useState({ src: false, dst: false });
    const [created, setCreated] = useState(false);
    const [createError, setCreateError] = useState(null);
    const [activeHelpField, setActiveHelpField] = useState(null);
    const [testResultSrc, setTestResultSrc] = useState(null);
    const [testResultDst, setTestResultDst] = useState(null);

    // Auto-test source and destination connections when entering Step 3 (Review Configuration)
    useEffect(() => {
        if (isOpen && step === 3) {
            if (!testResultSrc && source.host && source.port && source.username) {
                handleTestConnection('source');
            }
            if (!testResultDst && dest.host && dest.port && dest.username) {
                handleTestConnection('destination');
            }
        }
    }, [isOpen, step]);

    const validateForm = (data, isDest = false) => {
        const errs = {};
        if (!data.name || !data.name.trim()) {
            errs.name = 'Connection name is required';
        } else {
            const nameLower = data.name.trim().toLowerCase();
            const currentId = data.id || editingData?.id;

            if (Array.isArray(existingConnections) && existingConnections.length > 0) {
                const duplicate = existingConnections.find(c =>
                    c.name &&
                    c.name.trim().toLowerCase() === nameLower &&
                    c.id !== currentId
                );
                if (duplicate) {
                    errs.name = `A connection named "${data.name.trim()}" already exists. Please enter a unique name.`;
                }
            }

            if (!editContext) {
                const otherConnName = isDest ? source.name : dest.name;
                if (otherConnName && otherConnName.trim().toLowerCase() === nameLower) {
                    errs.name = isDest
                        ? 'Destination connection name must be different from source connection name.'
                        : 'Source connection name must be different from destination connection name.';
                }
            }

            const nameVal = validateName(data.name, 'Connection Name');
            if (!nameVal.valid) {
                errs.name = nameVal.error;
            }
        }

        if (!data.database_engine) errs.database_engine = 'Database Engine is required';
        if (!data.region) errs.region = 'Region is required';
        if (!data.host || !data.host.trim()) {
            errs.host = 'Host / IP Address is required';
        } else {
            const hostVal = validateHost(data.host);
            if (!hostVal.valid) {
                errs.host = hostVal.error;
            }
        }
        if (!data.port) errs.port = 'Port is required';
        if (!data.username || !data.username.trim()) errs.username = 'Username is required';
        
        const isEditing = !!(data.id || editingData?.id);
        if (!data.password && !isEditing) errs.password = 'Password is required';
        return errs;
    };

    const scrollToFirstError = () => {
        setTimeout(() => {
            const firstErrorEl = document.querySelector('.form-control[style*="ef4444"]') || 
                                 document.querySelector('input[style*="ef4444"]') ||
                                 document.querySelector('.wizard-content');
            if (firstErrorEl) {
                firstErrorEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
                if (typeof firstErrorEl.focus === 'function') firstErrorEl.focus();
            }
        }, 60);
    };

    const handleTestConnection = async (role) => {
        const isSrc = role === 'source';
        const connData = isSrc ? source : dest;
        const setErrors = isSrc ? setSrcErrors : setDstErrors;
        const setResult = isSrc ? setTestResultSrc : setTestResultDst;

        const errors = validateForm(connData, !isSrc);
        if (Object.keys(errors).length > 0) {
            setErrors({ ...errors, _forceTouch: true });
            scrollToFirstError();
            return;
        }

        setResult({ testing: true });
        try {
            const res = await fetch(`${API_BASE_URL}/api/dms/test-connection-direct`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(connData)
            });

            const contentType = res.headers.get('content-type') || '';
            if (!contentType.includes('application/json')) {
                setResult({ testing: false, success: false, message: `Host "${connData.host}" could not be reached. Please check the hostname / IP address.` });
                return;
            }

            const data = await res.json();
            const rawError = data.error || (Array.isArray(data.errors) && data.errors.length > 0 ? data.errors.join('; ') : '');

            if (data.success) {
                setResult({ testing: false, success: true, message: `Connected successfully to ${connData.host}:${connData.port}` });
            } else {
                let cleanError = rawError || 'Connection test failed. Verify host, port, credentials & firewall.';
                if (rawError.includes('ENOTFOUND') || rawError.includes('getaddrinfo')) {
                    cleanError = `Host "${connData.host}" could not be resolved. Please check the hostname / IP address.`;
                } else if (rawError.includes('ECONNREFUSED')) {
                    cleanError = `Connection refused at ${connData.host}:${connData.port}. Ensure the database is running and port ${connData.port} is open.`;
                } else if (rawError.includes('ETIMEDOUT') || rawError.includes('timeout')) {
                    cleanError = `Connection timed out reaching ${connData.host}:${connData.port}. Please check network / firewall settings.`;
                } else if (rawError.includes('password authentication failed') || rawError.includes('Access denied for user')) {
                    cleanError = `Authentication failed for user "${connData.username}". Please check username and password.`;
                }
                setResult({ testing: false, success: false, message: cleanError, suggestions: data.suggestions });
            }
        } catch (err) {
            setResult({ testing: false, success: false, message: 'Unable to reach backend service to test connection.' });
        }
    };

    // Pre-fill when editing
    useEffect(() => {
        if (isOpen && editingData) {
            const isDest = editContext === 'destination' || editingData.role === 'destination';
            const norm = normalizeConnectionData(editingData);
            if (isDest) setDest({ ...emptyConn('destination'), ...norm });
            else setSource({ ...emptyConn('source'), ...norm });
            setStep(isDest ? 2 : 1);
            setTestResultSrc(null);
            setTestResultDst(null);
        } else if (isOpen && !editingData) {
            setSource(emptyConn('source'));
            setDest(emptyConn('destination'));
            setStep(1);
            setSrcErrors({});
            setDstErrors({});
            setTouched({ src: false, dst: false });
            setCreated(false);
            setCreateError(null);
            setActiveHelpField(null);
            setTestResultSrc(null);
            setTestResultDst(null);
        }
    }, [isOpen, editingData, editContext]);

    // Live re-validation
    useEffect(() => {
        setSrcErrors(prev => ({ ...validateForm(source, false), _forceTouch: prev._forceTouch }));
        setTestResultSrc(null);
    }, [source]);
    useEffect(() => {
        setDstErrors(prev => ({ ...validateForm(dest, true), _forceTouch: prev._forceTouch }));
        setTestResultDst(null);
    }, [dest]);

    const goNext = () => {
        if (step === 1) {
            const e = validateForm(source);
            if (Object.keys(e).length > 0) {
                setSrcErrors({ ...e, _forceTouch: true });
                scrollToFirstError();
                return;
            }
        }
        if (step === 2) {
            const e = validateForm(dest, true);
            if (Object.keys(e).length > 0) {
                setDstErrors({ ...e, _forceTouch: true });
                scrollToFirstError();
                return;
            }
        }
        setStep(s => Math.min(4, s + 1));
        setActiveHelpField(null);
    };

    const goBack = () => {
        setStep(s => Math.max(1, s - 1));
        setActiveHelpField(null);
    };

    const handleCreate = async () => {
        setCreateError(null);
        try {
            await onCreateConnections({ source, destination: dest });
            setCreated(true);
        } catch (err) {
            setCreateError(err?.message || 'An unexpected error occurred');
        }
    };

    const handleDraft = () => {
        // Connections drafts removed per user requirement
    };

    const handleCloseWithAutoSave = () => {
        onClose && onClose();
    };

    const srcOk = isValid(source);
    const dstOk = isValid(dest);

    const getStepClasses = (s) => {
        let classes = [];
        if (s === step) classes.push('active');

        if (s === 1 && testResultSrc?.success === false) classes.push('failed');
        else if (s === 2 && testResultDst?.success === false) classes.push('failed');
        else if (s === 1 && (step > 1 || testResultSrc?.success)) classes.push('completed');
        else if (s === 2 && (step > 2 || testResultDst?.success)) classes.push('completed');
        else if (s === 3 && step > 3) classes.push('completed');
        else if (s === 4 && created) classes.push('completed');

        if (classes.length === 0) classes.push('pending');

        return classes.join(' ');
    };

    const getStepSubtitle = (s) => {
        if (s === 1) {
            if (testResultSrc?.success === false) return 'Test Failed';
            if (testResultSrc?.success) return 'Tested & Verified';
            if (step > 1 && srcOk) return 'Configured';
            if (step === 1) return 'In Progress';
            return 'Pending';
        }
        if (s === 2) {
            if (testResultDst?.success === false) return 'Test Failed';
            if (testResultDst?.success) return 'Tested & Verified';
            if (step > 2 && dstOk) return 'Configured';
            if (step === 2) return 'In Progress';
            return 'Pending';
        }
        if (s === 3) {
            if (step > 3) return 'Completed';
            if (step === 3) return 'Reviewing';
            return 'Pending';
        }
        if (s === 4) {
            if (created) return 'Completed';
            if (step === 4) return 'Ready';
            return 'Pending';
        }
        return '';
    };

    const canContinue =
        (step === 1 && srcOk) ||
        (step === 2 && dstOk) ||
        step === 3;

    if (!isOpen) return null;

    return (
        <div className="modal-overlay wizard-modal-overlay" onClick={onClose}>
            <div className="wizard-modal" onClick={e => e.stopPropagation()}>

                {/* Header */}
                <div className="wizard-header">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                        <button
                            onClick={onClose}
                            style={{
                                background: 'none', border: 'none', cursor: 'pointer',
                                padding: '0.5rem', display: 'flex', alignItems: 'center',
                                color: 'var(--text-primary)', fontSize: '1.1rem',
                            }}
                        >
                            ←
                        </button>
                        <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 500 }}>
                            Add database connection
                        </h2>
                    </div>
                    <button onClick={handleCloseWithAutoSave} className="modal-close">
                        <X size={24} />
                    </button>
                </div>

                {/* Body */}
                <div className="wizard-body">

                    {/* ── Left Sidebar ── */}
                    {!editContext && (
                        <div className="wizard-sidebar">
                        {WIZARD_STEPS.map(ws => {
                            const statusClasses = getStepClasses(ws.id);
                            const isCompleted = statusClasses.includes('completed');
                            const isFailed = statusClasses.includes('failed');
                            return (
                                <div key={ws.id} className={`wizard-step-item ${statusClasses}`}>
                                    <div className="step-number" style={isFailed ? { background: '#ef4444', borderColor: '#ef4444', color: '#fff' } : undefined}>
                                        {isFailed ? '!' : isCompleted ? '✓' : ws.id}
                                    </div>
                                    <div className="step-content">
                                        <div className="step-title">{ws.shortTitle}</div>
                                        <div className="step-subtitle" style={{
                                            color: isFailed ? '#ef4444' : isCompleted ? '#10b981' : (ws.id === step ? 'var(--primary)' : 'var(--text-muted)'),
                                            fontWeight: isFailed || isCompleted || ws.id === step ? 600 : 400
                                        }}>
                                            {getStepSubtitle(ws.id)}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}

                        {/* Sidebar Actions */}
                        <div className="wizard-sidebar-actions">
                            <button
                                type="button"
                                className="btn-save-draft"
                                onClick={() => {
                                    if (onSaveDraft) onSaveDraft({ source, dest, step });
                                    onClose();
                                }}
                                disabled={!source.name && !dest.name}
                            >
                                <Save size={16} /> <span>Save and Exit</span>
                            </button>
                            <button
                                type="button"
                                className="btn-discard-draft"
                                onClick={onClose}
                            >
                                <Trash2 size={16} /> <span>Discard</span>
                            </button>
                        </div>
                    </div>
                    )}

                    {/* ── Main Content ── */}
                    <div className="wizard-content" style={{ width: editContext ? '100%' : undefined }}>

                        {/* Step 1 — Source */}
                        {step === 1 && (
                            <div className="wizard-step-content">
                                <div className="step-header">
                                    <h3>{WIZARD_STEPS[0].title}</h3>
                                    <p>{WIZARD_STEPS[0].description}</p>
                                </div>
                                <div className="step-body">
                                    <ConnectionForm
                                        data={source}
                                        onChange={setSource}
                                        errors={srcErrors}
                                        cloudRegions={cloudRegions}
                                        vpnStatus={vpnStatus}
                                        activeHelpField={activeHelpField}
                                        setActiveHelpField={setActiveHelpField}
                                        onNavigateToPrivateConnectivity={onNavigateToPrivateConnectivity}
                                    />
                                    {testResultSrc && !testResultSrc.testing && (
                                        <div style={{
                                            marginTop: '1rem',
                                            padding: '0.875rem 1rem',
                                            borderRadius: 10,
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: 6,
                                            fontSize: '0.85rem',
                                            background: testResultSrc.success ? 'rgba(16,185,129,0.08)' : 'rgba(239,68,68,0.08)',
                                            border: `1px solid ${testResultSrc.success ? 'rgba(16,185,129,0.3)' : 'rgba(239,68,68,0.3)'}`,
                                            color: testResultSrc.success ? '#10b981' : '#ef4444',
                                        }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontWeight: 600 }}>
                                                {testResultSrc.success ? <CheckCircle size={16} /> : <AlertCircle size={16} />}
                                                <span>{testResultSrc.message}</span>
                                            </div>
                                            {testResultSrc.suggestions?.length > 0 && (
                                                <ul style={{ margin: '4px 0 0 26px', padding: 0, fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                                                    {testResultSrc.suggestions.map((s, i) => <li key={i}>{s}</li>)}
                                                </ul>
                                            )}
                                        </div>
                                    )}
                                </div>
                                <div className="step-footer">
                                    <button className="btn-secondary" onClick={onClose} disabled={loading}>
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        className="btn-secondary"
                                        onClick={() => handleTestConnection('source')}
                                        disabled={loading || testResultSrc?.testing}
                                        style={{
                                            borderColor: testResultSrc?.success ? '#10b981' : testResultSrc?.success === false ? '#ef4444' : undefined,
                                            color: testResultSrc?.success ? '#10b981' : testResultSrc?.success === false ? '#ef4444' : undefined,
                                            marginRight: 'auto',
                                            marginLeft: 10,
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: 6
                                        }}
                                    >
                                        {testResultSrc?.testing ? (
                                            <><span className="spin" style={{ display: 'inline-flex' }}><Zap size={14} /></span> Testing…</>
                                        ) : testResultSrc?.success ? (
                                            <><CheckCircle size={14} /> Tested Successfully</>
                                        ) : (
                                            <><Zap size={14} /> Test Connection</>
                                        )}
                                    </button>
                                    {editContext ? (
                                        <button
                                            className="btn-primary"
                                            onClick={() => {
                                                const e = validateForm(source);
                                                if (Object.keys(e).length > 0) {
                                                    setSrcErrors({ ...e, _forceTouch: true });
                                                    return;
                                                }
                                                onUpdateSingleConnection && onUpdateSingleConnection(source);
                                            }}
                                            disabled={loading}
                                        >
                                            {loading ? 'Saving...' : 'Save & Exit'}
                                        </button>
                                    ) : (
                                        <button
                                            className="btn-primary"
                                            onClick={goNext}
                                        >
                                            Continue →
                                        </button>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Step 2 — Destination */}
                        {step === 2 && (
                            <div className="wizard-step-content">
                                <div className="step-header">
                                    <h3>{WIZARD_STEPS[1].title}</h3>
                                    <p>{WIZARD_STEPS[1].description}</p>
                                </div>
                                <div className="step-body">
                                    <ConnectionForm
                                        data={dest}
                                        onChange={setDest}
                                        errors={dstErrors}
                                        cloudRegions={cloudRegions}
                                        vpnStatus={vpnStatus}
                                        activeHelpField={activeHelpField}
                                        setActiveHelpField={setActiveHelpField}
                                        onNavigateToPrivateConnectivity={onNavigateToPrivateConnectivity}
                                    />
                                    {testResultDst && !testResultDst.testing && (
                                        <div style={{
                                            marginTop: '1rem',
                                            padding: '0.875rem 1rem',
                                            borderRadius: 10,
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: 6,
                                            fontSize: '0.85rem',
                                            background: testResultDst.success ? 'rgba(16,185,129,0.08)' : 'rgba(239,68,68,0.08)',
                                            border: `1px solid ${testResultDst.success ? 'rgba(16,185,129,0.3)' : 'rgba(239,68,68,0.3)'}`,
                                            color: testResultDst.success ? '#10b981' : '#ef4444',
                                        }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontWeight: 600 }}>
                                                {testResultDst.success ? <CheckCircle size={16} /> : <AlertCircle size={16} />}
                                                <span>{testResultDst.message}</span>
                                            </div>
                                            {testResultDst.suggestions?.length > 0 && (
                                                <ul style={{ margin: '4px 0 0 26px', padding: 0, fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                                                    {testResultDst.suggestions.map((s, i) => <li key={i}>{s}</li>)}
                                                </ul>
                                            )}
                                        </div>
                                    )}
                                </div>
                                <div className="step-footer">
                                    <button className="btn-secondary" onClick={editContext ? onClose : goBack} disabled={loading}>
                                        {editContext ? 'Cancel' : '← Back'}
                                    </button>
                                    <button
                                        type="button"
                                        className="btn-secondary"
                                        onClick={() => handleTestConnection('destination')}
                                        disabled={loading || testResultDst?.testing}
                                        style={{
                                            borderColor: testResultDst?.success ? '#10b981' : testResultDst?.success === false ? '#ef4444' : undefined,
                                            color: testResultDst?.success ? '#10b981' : testResultDst?.success === false ? '#ef4444' : undefined,
                                            marginRight: 'auto',
                                            marginLeft: 10,
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: 6
                                        }}
                                    >
                                        {testResultDst?.testing ? (
                                            <><span className="spin" style={{ display: 'inline-flex' }}><Zap size={14} /></span> Testing…</>
                                        ) : testResultDst?.success ? (
                                            <><CheckCircle size={14} /> Tested Successfully</>
                                        ) : (
                                            <><Zap size={14} /> Test Connection</>
                                        )}
                                    </button>
                                    {editContext ? (
                                        <button
                                            className="btn-primary"
                                            onClick={() => {
                                                const e = validateForm(dest, true);
                                                if (Object.keys(e).length > 0) {
                                                    setDstErrors({ ...e, _forceTouch: true });
                                                    return;
                                                }
                                                onUpdateSingleConnection && onUpdateSingleConnection(dest);
                                            }}
                                            disabled={loading}
                                        >
                                            {loading ? 'Saving...' : 'Save & Exit'}
                                        </button>
                                    ) : (
                                        <button
                                            className="btn-primary"
                                            onClick={goNext}
                                        >
                                            Continue →
                                        </button>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Step 3 — Review */}
                        {step === 3 && (
                            <div className="wizard-step-content">
                                <div className="step-header">
                                    <h3>{WIZARD_STEPS[2].title}</h3>
                                    <p>{WIZARD_STEPS[2].description}</p>
                                </div>
                                <div className="step-body">
                                    <ReviewPanel
                                        source={source}
                                        dest={dest}
                                        onEditSource={() => setStep(1)}
                                        onEditDest={() => setStep(2)}
                                    />
                                </div>
                                <div className="step-footer">
                                    <button className="btn-secondary" onClick={goBack}>
                                        ← Back
                                    </button>
                                    <button
                                        className="btn-primary"
                                        onClick={goNext}
                                    >
                                        Continue →
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Step 4 — Create */}
                        {step === 4 && (
                            <div className="wizard-step-content">
                                <div className="step-header">
                                    <h3>{WIZARD_STEPS[3].title}</h3>
                                    <p>{WIZARD_STEPS[3].description}</p>
                                </div>
                                <div className="step-body">
                                    <CreateStep
                                        loading={loading}
                                        created={created}
                                        error={createError}
                                    />
                                </div>
                                <div className="step-footer">
                                    {!created && (
                                        <>
                                            <button className="btn-secondary" onClick={goBack} disabled={loading}>
                                                ← Back
                                            </button>
                                            <button
                                                className="btn-primary"
                                                onClick={handleCreate}
                                                disabled={loading}
                                                style={{
                                                    background: loading
                                                        ? 'rgba(16,185,129,0.4)'
                                                        : 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
                                                    boxShadow: loading ? 'none' : '0 4px 15px rgba(16,185,129,0.3)',
                                                }}
                                            >
                                                {loading
                                                    ? <><span className="spin" style={{ display: 'inline-flex' }}><Zap size={16} /></span> Creating…</>
                                                    : <><Check size={16} /> Create Connection</>
                                                }
                                            </button>
                                        </>
                                    )}
                                    {created && (
                                        <button className="btn-primary" onClick={onClose}>
                                            Done ✓
                                        </button>
                                    )}
                                </div>
                            </div>
                        )}

                    </div>
                </div>
            </div>
        </div>
    );
}
